/**
 * The invite code on every sign-in door, and what happens when a new person has none
 * (GH-871b, DEC-052; owner, 2026-10-03: *"users should only be able to sign up / login if they
 * have an invite code"*).
 *
 * Three pieces, used by the three hosts of `SignInChoices` (the donor sheet, the check-in page and
 * the membership sign-in sheet):
 *
 *  - `useSignUpAccess` — the code typed or carried in (prefilled from `/i/<code>`), and the big
 *    screen's join token (`?join=`), sent as `inviteCode` / `eventJoinToken` on every door: phone,
 *    Apple and Google.
 *  - `InviteCodeEntry` — "Have an invite?" under the doors, opening the field. Drawn only while
 *    membership is on (the pulse answers); with it off the sheet is exactly as before.
 *  - `InviteRequiredStep` — `invite_required` or `invite_unavailable`: the person stays on the
 *    sheet, with the code field and the waitlist without an account. Entering a code sends the
 *    same confirmation again (`retryDoor`): the phone code or the provider's nonce was not spent.
 *
 * An existing account signs in exactly as before; the server ignores both fields for it.
 */
import { useState } from "react";
import {
  ACCESS_COPY,
  inviteCodeSchema,
  MEMBERSHIP_COPY,
  type DonorSessionResponse,
} from "@givehere/contracts";
import { AccountlessWaitlist } from "./AccountlessWaitlist.js";
import type { DonorApiError, SignUpAccess } from "./donor-session.js";
import { InviteCodeForm, redeemFailure } from "./InviteCodeForm.js";
import { useMembershipPulse } from "./membership-hooks.js";
import { retryDoor, type DoorAttempt } from "./sign-up-access.js";

export type SignUpAccessState = {
  /** Membership is on here (the pulse answered); the code field is offered only then. */
  readonly gateOpen: boolean;
  readonly raw: string;
  readonly setRaw: (raw: string) => void;
  readonly open: boolean;
  readonly setOpen: (open: boolean) => void;
  /** What the next sign-in carries: a well-formed code, the join token — or nothing. */
  readonly fields: () => SignUpAccess;
};

export function useSignUpAccess({
  prefillCode = null,
  joinToken = null,
  enabled = true,
}: {
  prefillCode?: string | null;
  joinToken?: string | null;
  /** False where nobody will sign in (already signed in): nothing is fetched. */
  enabled?: boolean;
} = {}): SignUpAccessState {
  const pulse = useMembershipPulse(enabled);
  const [raw, setRaw] = useState(prefillCode ?? "");
  const [open, setOpen] = useState(Boolean(prefillCode));
  const fields = (): SignUpAccess => {
    const parsed = raw.trim() ? inviteCodeSchema.safeParse(raw) : null;
    return {
      ...(parsed?.success ? { inviteCode: parsed.data } : {}),
      ...(joinToken ? { eventJoinToken: joinToken } : {}),
    };
  };
  return {
    gateOpen: pulse !== null && pulse !== undefined,
    raw,
    setRaw,
    open,
    setOpen,
    fields,
  };
}

/** "Have an invite?" and the field it opens, under the doors. Nothing while membership is off. */
export function InviteCodeEntry({ access }: { access: SignUpAccessState }) {
  const [touched, setTouched] = useState(false);
  if (!access.gateOpen) return null;
  if (!access.open) {
    return (
      <button type="button" className="link landing__quiet" onClick={() => access.setOpen(true)}>
        {MEMBERSHIP_COPY.haveInvite}
      </button>
    );
  }
  const invalid =
    touched && access.raw.trim() !== "" && !inviteCodeSchema.safeParse(access.raw).success;
  return (
    <>
      <label className="field invite-entry">
        <span>{MEMBERSHIP_COPY.enterInviteCode}</span>
        <input
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          maxLength={32}
          value={access.raw}
          aria-invalid={invalid || undefined}
          aria-describedby={invalid ? "invite-entry-error" : undefined}
          onBlur={() => setTouched(true)}
          onChange={(changed) => access.setRaw(changed.target.value)}
        />
      </label>
      {invalid && (
        <p id="invite-entry-error" className="notice notice--error" role="alert">
          That isn’t an invite code. Check it and try again.
        </p>
      )}
    </>
  );
}

/** The sentence for a refusal as the sheet first shows it. */
const refusalLine = (refusal: DonorApiError): string | null =>
  refusal.code === "invite_unavailable" ? MEMBERSHIP_COPY.inviteUnavailable : null;

export function InviteRequiredStep({
  attempt,
  refusal,
  access,
  headingId,
  onIssued,
  onRestart,
  waitlist = "inline",
}: {
  attempt: DoorAttempt;
  refusal: DonorApiError;
  access: SignUpAccessState;
  headingId?: string | undefined;
  /** The device is remembered; the host carries on as after any sign-in. */
  onIssued: (issued: DonorSessionResponse) => Promise<void> | void;
  /** Back to the doors, for a code that has lapsed or a different way in. */
  onRestart: () => void;
  /**
   * `inline` in a sheet. `link` on a page about an event (the check-in page), which sends the
   * person to `/waitlist` — a place in line is never drawn on an event screen (DEC-051).
   */
  waitlist?: "inline" | "link";
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(() => refusalLine(refusal));
  const [joining, setJoining] = useState(false);

  if (joining) {
    return (
      <AccountlessWaitlist
        heading={MEMBERSHIP_COPY.joinWaitlist}
        headingId={headingId}
        initialContact={attempt.door === "phone" ? attempt.phone : ""}
        onSignIn={onRestart}
      />
    );
  }

  const retry = (code: string) => {
    setError(null);
    setBusy(true);
    retryDoor(attempt, { ...access.fields(), inviteCode: code })
      .then((issued) => onIssued(issued))
      .catch((caught: unknown) => setError(redeemFailure(caught)))
      .finally(() => setBusy(false));
  };

  return (
    <>
      <h2 id={headingId} className="donor__title">
        {ACCESS_COPY.inviteRequired}
      </h2>
      <p className="donor__where">{ACCESS_COPY.inviteRequiredBody}</p>
      <InviteCodeForm busy={busy} initial={access.raw} onSubmit={retry} />
      {error && (
        <p className="notice notice--error" role="alert">
          {error}
        </p>
      )}
      {waitlist === "inline" ? (
        <button type="button" className="pay pay--outline" onClick={() => setJoining(true)}>
          {MEMBERSHIP_COPY.joinWaitlist}
        </button>
      ) : (
        <a className="pay pay--outline landing__give" href="/waitlist">
          {MEMBERSHIP_COPY.joinWaitlist}
        </a>
      )}
      <button type="button" className="link landing__quiet" onClick={onRestart}>
        Start again
      </button>
    </>
  );
}
